import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { InputWithLabelAndError as Input } from "components/ui/InputWithLabelAndError";
import { Icon } from "@iconify/react";
import { inviteProjectUser } from "helpers/apiClient";

const initialForm = {
  username: "",
  name: "",
  lastName: "",
  phone: "",
  email: "",
  role: "regante",
  parcelId: "",
};

const parcelLabel = (parcel) => {
  const references = [
    parcel.irrigationReference && `Parcela ${parcel.irrigationReference}`,
    parcel.cadastralReference && `Catastro ${parcel.cadastralReference}`,
    parcel.polygonReference && `Polígono ${parcel.polygonReference}`,
  ].filter(Boolean);
  return references.join(" · ") || parcel.id;
};

const CreateUser = ({ projectId, projectName = "el proyecto actual", availableParcels = [], onInvited }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [requestError, setRequestError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.username.trim()) next.username = "El nombre de usuario es obligatorio.";
    if (!form.name.trim()) next.name = "El nombre es obligatorio.";
    if (!form.lastName.trim()) next.lastName = "Los apellidos son obligatorios.";
    if (!/\S+@\S+\.\S+/.test(form.email)) next.email = "El email no es válido.";
    if (form.phone && !/^\+[1-9]\d{7,14}$/.test(form.phone)) next.phone = "Usa formato internacional, por ejemplo +34600111222.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (isSubmitting || !validate()) return;
    setIsSubmitting(true);
    setRequestError("");
    try {
      const payload = {
        username: form.username.trim(),
        name: form.name.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        ...(form.parcelId ? { parcelId: form.parcelId } : {}),
      };
      const result = await inviteProjectUser(projectId, payload);
      await onInvited?.(result);
      setForm(initialForm);
      setOpen(false);
    } catch (error) {
      setRequestError(error.message || "No se ha podido crear la invitación.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !isSubmitting && setOpen(nextOpen)}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" className="p-1.5 px-2" aria-label="Crear nuevo usuario">
          <Icon icon="mdi:plus" className="size-5" />
        </Button>
      </DialogTrigger>

      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[calc(100vh-2rem)] overflow-x-hidden overflow-y-auto p-5 sm:p-6 dark:text-white">
        <DialogHeader>
          <DialogTitle>Crear nuevo Usuario</DialogTitle>
          <DialogDescription>
            La persona recibirá un enlace de un solo uso para crear su propia contraseña en {projectName}.
          </DialogDescription>
        </DialogHeader>

        <form className="grid grid-cols-1 gap-3 sm:grid-cols-2" onSubmit={submit}>
          <div className="grid min-w-0 gap-3 sm:col-span-2 sm:grid-cols-2">
            <Input containerClassName="min-w-0" className="w-full" name="username" label="Nombre de usuario" value={form.username} onChange={update} error={errors.username} />
            <Input containerClassName="min-w-0" className="w-full" name="name" label="Nombre" value={form.name} onChange={update} error={errors.name} />
            <Input containerClassName="min-w-0" className="w-full" name="lastName" label="Apellido(s)" value={form.lastName} onChange={update} error={errors.lastName} />
            <Input containerClassName="min-w-0" className="w-full" name="phone" label="Teléfono" value={form.phone} onChange={update} error={errors.phone} placeholder="+34600111222" />
          </div>
          <Input containerClassName="min-w-0 sm:col-span-2" className="w-full" name="email" type="email" label="Email" value={form.email} onChange={update} error={errors.email} />
          <label className="flex min-w-0 flex-col gap-1 text-sm font-bold">
            Rol
            <select name="role" value={form.role} onChange={update} className="h-9 w-full min-w-0 rounded-md border border-gray-200 bg-white px-3 dark:border-gray-800 dark:bg-gray-900">
              <option value="admin">Gestor</option>
              <option value="tecnico">Técnico de campo</option>
              <option value="regante">Regante</option>
            </select>
          </label>
          <label className="flex min-w-0 flex-col gap-1 text-sm font-bold">
            Parcela (opcional)
            <select name="parcelId" value={form.parcelId} onChange={update} className="h-9 w-full min-w-0 rounded-md border border-gray-200 bg-white px-3 dark:border-gray-800 dark:bg-gray-900">
              <option value="">Sin parcela asignada</option>
              {availableParcels.map((parcel) => <option key={parcel.id} value={parcel.id}>{parcelLabel(parcel)}</option>)}
            </select>
          </label>
          <p className="text-xs text-gray-500 sm:col-span-2">Catastro y polígono proceden de la parcela existente; no se guardan como datos libres del usuario.</p>
          {requestError && <p role="alert" className="text-sm text-red-600 sm:col-span-2">{requestError}</p>}
          <DialogFooter className="flex justify-between sm:col-span-2">
            <Button type="button" variant="secondary" disabled={isSubmitting} onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" variant="positive" disabled={isSubmitting || !projectId}>
              {isSubmitting ? "Enviando invitación..." : "Guardar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUser;
